import type { CategoryViewProps, GameMinimalProps } from '../../types';

import React from 'react';
import styled from '@emotion/styled';
import dynamic from 'next/dynamic';
import { useTheme } from '@mui/material/styles';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../contexts/AppContext';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Typography as MuiTypography } from '@mui/material';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Card, CardSkeleton } from '../../components/Card';
import { Title } from '../../components/Title';
import { BreadCrumbs } from '../../components/BreadCrumbs';
import { CollectionSection } from '../../components/CollectionSection';
import { CardTypeEnum } from '../../types/enum';
import { useCursorContext } from '../../contexts/CursorContext';

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const CarouselList = dynamic<any>(() => import('../../components/CarouselList').then((mod) => mod.CarouselList), {
  ssr: false,
});

const CardSkeletonStyled = styled(CardSkeleton)(({ theme }) => ({
  boxShadow: theme.shadows[0],
  aspectRatio: '16/9',
}));

export const CollectionC = (props: CategoryViewProps) => {
  const { title, body, collection, global, pageContext } = props;
  const { t } = useTranslation('collection_c');
  const { ref, inView } = useInView();
  const appContext = useAppContext();
  const { enabled } = useCursorContext();
  const { assetUrl, componentOptions } = appContext.useConfig();
  const theme = useTheme();
  const descriptionRef = React.useRef<HTMLInputElement | null>(null);

  const totalPreferredItems = 18;
  const totalItems = collection.games.length || 0;
  const itemsLeft = totalPreferredItems - totalItems;
  const emptyItems = itemsLeft < 0 ? 0 : itemsLeft;
  const cardType = componentOptions?.component_game_carousel_card_type;
  const hideEmptyCards = componentOptions?.component_empty_cards_hidden;

  const breaks = {
    xs: 6,
    sm: 4,
    md: 3,
    lg: 2,
  };

  return (
    <>
      <Container maxWidth={false}>
        <BreadCrumbs breadcrumbs={pageContext.breadcrumbs} hidden={componentOptions?.component_breadcrumb_hidden} />
      </Container>
      <>
        <Container ref={ref} maxWidth={false} disableGutters={false}>
          <CollectionSection sx={{ paddingBottom: theme.spacing(2) }}>
            {title && (
              <Title component="h1" variant="h1" disableGutters>
                {title}
              </Title>
            )}
            {body && (
              <Typography
                ref={descriptionRef}
                variant="body1"
                sx={{ marginTop: theme.spacing(1.25), marginBottom: theme.spacing(1) }}
              >
                {body}
              </Typography>
            )}
            <Grid container spacing={2} sx={{ paddingTop: body ? theme.spacing(1) : 0 }}>
              {collection.games.map((game: GameMinimalProps, index: number) => (
                <Grid key={game.id || index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
                  <Card
                    key={game.name}
                    href={`${enabled ? '#' : `/game/${game.slug}`}`}
                    src={`${assetUrl}${game.images.cover16x9 || game.images.cover}/`}
                    alt={game.name || 'Cover'}
                    title={game.name || undefined}
                    description={game.description || undefined}
                    width={componentOptions.component_card_width || 512}
                    height={componentOptions.component_card_height || 288}
                    sizes={`${theme.breakpoints.up('lg').replace('@media ', '')} 16.666vw, ${theme.breakpoints
                      .up('md')
                      .replace('@media ', '')} 25vw, ${theme.breakpoints
                      .up('sm')
                      .replace('@media ', '')} 33.333vw, 50vw`}
                    layout="responsive"
                    template={cardType || CardTypeEnum.SIMPLE}
                    priority={index === 0}
                  />
                </Grid>
              ))}
              {!hideEmptyCards && [...Array(emptyItems)].map((item, index) => (
                <Grid key={index} xs={breaks.xs} sm={breaks.sm} md={breaks.md} lg={breaks.lg} item>
                  <CardSkeletonStyled elevation={1} />
                </Grid>
              ))}
            </Grid>
          </CollectionSection>
        </Container>
        {inView && Array.isArray(global.popular) && global.popular.length > 0 && (
          <CollectionSection>
            <Title component="h2" variant="h2">
              {t('popular_header', 'Popular')}
            </Title>
            <CarouselList
              padding={theme.spacing(2)}
              items={global.popular}
              cardOptions={{
                width: componentOptions.component_card_width || 512,
                height: componentOptions.component_card_height || 288,
                sizes: '(min-width:1200px) 16.666vw, (min-width:900px) 25vw, (min-width:600px) 33.333vw, 50vw',
                layout: 'responsive',
                template: 'simple',
                typographyOptions: {
                  align: 'center',
                },
              }}
            />
          </CollectionSection>
        )}
      </>
    </>
  );
}